import React, {
	useState,
	useEffect,
	useRef,
	useCallback,
	ChangeEvent,
	KeyboardEvent
} from "react";
import ZDrawer from "@molecules/ZDrawer";
import { Input } from "antd";
import { ZButton, ZIcon, ZTypography } from "@atoms";
import { EIcons } from "@atoms/ZIcon/interface";
import { EColors } from "@atoms/ZColors/interface";
import { EFontWeights } from "@atoms/ZTypography/interface";
import ZMessage from "@molecules/ZMessage";
import PricingField from "./PricingField";
import { ZPricingConfigurationDrawerProps, PricingConfig } from "./interface";
import "./index.less";

const MAX_IMAGE_COUNT = 5;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

const validatePercentage = (v: string) => {
	if (!v) return "";
	const n = Number(v);
	return Number.isNaN(n) || n < 0 || n > 100
		? "Must be between 0 and 100"
		: "";
};

const validateNumber = (v: string) => {
	if (!v) return "";
	return Number.isNaN(Number(v)) ? "Must be a number" : "";
};

const calculateMarkupFromMargin = (marginStr: string): string => {
	const margin = Number(marginStr);
	if (!marginStr || Number.isNaN(margin) || margin >= 100) return "";
	return String(Math.round(((100 * margin) / (100 - margin)) * 100) / 100);
};

const calculateMarginFromMarkup = (markupStr: string): string => {
	const markup = Number(markupStr);
	if (!markupStr || Number.isNaN(markup) || markup <= -100) return "";
	return String(Math.round(((100 * markup) / (100 + markup)) * 100) / 100);
};

const ACCEPTED_IMAGE_TYPES = ".jpg,.jpeg,.png";

interface PromptTag {
	id: string;
	label: string;
	text: string;
}

const PROMPT_TAGS: PromptTag[] = [
	{
		id: "set-name",
		label: "Set Name",
		text: 'Set the name field to "{{Manufacturer/Vendor Name}} – {{Manufacturer Part Number OR SKU}}".'
	},
	{
		id: "set-description",
		label: "Set Description",
		text: 'Do not use the product description field found in the file. Instead set the product description to "{{Product Name found in the file}} - {{Manufacturer Part Number OR SKU}}"'
	},
	{
		id: "include-0-value",
		label: "Include 0 Value Items",
		text: "Include items where the quantity or selling price is 0, NA, or N/A."
	},
	{
		id: "set-quantity",
		label: "Set Quantity",
		text: "After grouping, set the quantity to 1 for all products and adjust the unit selling price so the total amount remains unchanged."
	},
	{
		id: "override-billing-period",
		label: "Override Billing Period",
		text: "Set the billing period to one-time for all products."
	},
	{
		id: "do-not-group",
		label: "Do Not Group",
		text: "Do not group products. Keep all items as separate line items."
	},
	{
		id: "only-group-by-mpn",
		label: "Only Group by MPN",
		text: "Group products only when the Manufacturer Part Number (MPN) matches exactly, ignoring case differences."
	}
];

const ZPricingConfigurationDrawer: React.FC<
	ZPricingConfigurationDrawerProps
> = ({ visible, onClose, onSkip, onConfirm, initialValues, error }) => {
	const [values, setValues] = useState<PricingConfig>({
		targetMargin: "",
		markup: "",
		pricingMode: "margin",
		lineItemDiscount: "",
		userPreferences: "",
		additionalImages: []
	});

	const [activeTags, setActiveTags] = useState<Set<string>>(new Set());
	const [modifiedTags, setModifiedTags] = useState<Set<string>>(new Set());

	const historyRef = useRef<string[]>([""]);
	const historyIndexRef = useRef(0);
	const isUndoRedoRef = useRef(false);

	const [validation, setValidation] = useState({
		targetMargin: "",
		markup: "",
		lineItemDiscount: ""
	});

	useEffect(() => {
		if (!initialValues) return;

		setValues({
			targetMargin: initialValues.targetMargin || "",
			markup: initialValues.markup || "",
			pricingMode: initialValues.pricingMode || "margin",
			lineItemDiscount: initialValues.lineItemDiscount || "",
			userPreferences: initialValues.userPreferences || "",
			additionalImages: initialValues.additionalImages || []
		});

		setValidation({
			targetMargin: "",
			markup: "",
			lineItemDiscount: ""
		});

		setActiveTags(new Set());
		setModifiedTags(new Set());

		const initial = initialValues?.userPreferences || "";
		historyRef.current = [initial];
		historyIndexRef.current = 0;
	}, [initialValues, visible]);

	const handleMarginChange = (e: ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		const autoMarkup = calculateMarkupFromMargin(value);
		setValues(prev => ({
			...prev,
			targetMargin: value,
			markup: autoMarkup,
			pricingMode: "margin"
		}));
		setValidation(prev => ({
			...prev,
			targetMargin: validatePercentage(value)
		}));
	};

	const handleMarkupChange = (e: ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		const autoMargin = calculateMarginFromMarkup(value);
		setValues(prev => ({
			...prev,
			markup: value,
			targetMargin: autoMargin,
			pricingMode: "markup"
		}));
		setValidation(prev => ({
			...prev,
			markup: validateNumber(value)
		}));
	};

	const handleSwitchToMarkup = () => {
		setValues(prev => {
			const autoMargin = calculateMarginFromMarkup(prev.markup);
			return {
				...prev,
				pricingMode: "markup",
				targetMargin: autoMargin
			};
		});
		setValidation(prev => ({ ...prev, targetMargin: "" }));
	};

	const handleSwitchToMargin = () => {
		setValues(prev => {
			const autoMarkup = calculateMarkupFromMargin(prev.targetMargin);
			return {
				...prev,
				pricingMode: "margin",
				markup: autoMarkup
			};
		});
		setValidation(prev => ({ ...prev, markup: "" }));
	};

	const handleLineItemDiscountChange = (e: ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setValues(prev => ({ ...prev, lineItemDiscount: value }));
		setValidation(prev => ({
			...prev,
			lineItemDiscount: validatePercentage(value)
		}));
	};

	const hasTagFragment = (tagText: string, value: string): boolean => {
		const words = tagText
			.split(/\s+/)
			.map(w => w.replace(/[^a-zA-Z]/g, "").toLowerCase())
			.filter(w => w.length >= 6);
		return words.some(word => value.toLowerCase().includes(word));
	};

	const syncPillStates = useCallback(
		(
			text: string,
			previousActiveTags: Set<string>,
			previousModifiedTags: Set<string>
		) => {
			const newActiveTags = new Set<string>();
			const newModifiedTags = new Set<string>();
			PROMPT_TAGS.forEach(tag => {
				if (text.includes(tag.text)) {
					newActiveTags.add(tag.id);
				} else if (
					previousActiveTags.has(tag.id) ||
					previousModifiedTags.has(tag.id)
				) {
					if (hasTagFragment(tag.text, text)) {
						newModifiedTags.add(tag.id);
					}
				}
			});
			setActiveTags(newActiveTags);
			setModifiedTags(newModifiedTags);
		},
		[]
	);

	const pushHistory = useCallback((text: string) => {
		if (isUndoRedoRef.current) return;
		const idx = historyIndexRef.current;
		const history = historyRef.current.slice(0, idx + 1);
		history.push(text);
		historyRef.current = history;
		historyIndexRef.current = history.length - 1;
	}, []);

	const handleUndoRedo = useCallback(
		(e: KeyboardEvent<HTMLTextAreaElement>) => {
			const isUndo =
				(e.metaKey || e.ctrlKey) && !e.shiftKey && e.key === "z";
			const isRedo =
				(e.metaKey || e.ctrlKey) && e.shiftKey && e.key === "z";

			if (!isUndo && !isRedo) return;
			e.preventDefault();

			const idx = historyIndexRef.current;
			const history = historyRef.current;

			if (isUndo && idx <= 0) return;
			if (isRedo && idx >= history.length - 1) return;

			const newIdx = isUndo ? idx - 1 : idx + 1;
			historyIndexRef.current = newIdx;
			const restoredText = history[newIdx];

			isUndoRedoRef.current = true;
			setValues(prev => ({ ...prev, userPreferences: restoredText }));
			syncPillStates(restoredText, activeTags, modifiedTags);
			isUndoRedoRef.current = false;
		},
		[activeTags, modifiedTags, syncPillStates]
	);

	const handleTagToggle = (tag: PromptTag) => {
		const isActive = activeTags.has(tag.id);
		const isModified = modifiedTags.has(tag.id);
		if (isActive) {
			const newText = values.userPreferences
				.split("\n")
				.filter(line => line.trim() !== tag.text.trim())
				.join("\n")
				.trim();
			pushHistory(newText);
			setValues(prev => ({ ...prev, userPreferences: newText }));
			setActiveTags(prev => {
				const next = new Set(prev);
				next.delete(tag.id);
				return next;
			});
		} else if (isModified) {
			setModifiedTags(prev => {
				const next = new Set(prev);
				next.delete(tag.id);
				return next;
			});
		} else {
			const separator = values.userPreferences.trim() ? "\n" : "";
			const newText = (
				values.userPreferences.trim() +
				separator +
				tag.text
			).trim();
			pushHistory(newText);
			setValues(prev => ({
				...prev,
				userPreferences: newText
			}));
			setActiveTags(prev => new Set([...prev, tag.id]));
		}
	};

	const handleUserPreferencesChange = (
		e: ChangeEvent<HTMLTextAreaElement>
	) => {
		const newValue = e.target.value;
		pushHistory(newValue);
		setValues(prev => ({ ...prev, userPreferences: newValue }));
		syncPillStates(newValue, activeTags, modifiedTags);
	};

	const handleImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (!files || files.length === 0) return;

		const currentCount = values.additionalImages.length;
		if (currentCount >= MAX_IMAGE_COUNT) {
			ZMessage.error(`Maximum ${MAX_IMAGE_COUNT} images allowed`);
			e.target.value = "";
			return;
		}

		const fileArray = Array.from(files);
		const oversized = fileArray.filter(f => f.size > MAX_IMAGE_SIZE);
		if (oversized.length > 0) {
			ZMessage.error("Each image must be less than 5MB");
		}

		const validFiles = fileArray.filter(f => f.size <= MAX_IMAGE_SIZE);
		const remaining = MAX_IMAGE_COUNT - currentCount;
		const accepted = validFiles.slice(0, remaining);

		if (validFiles.length > remaining) {
			ZMessage.error(`Maximum ${MAX_IMAGE_COUNT} images allowed`);
		}

		if (accepted.length > 0) {
			setValues(prev => ({
				...prev,
				additionalImages: [...prev.additionalImages, ...accepted]
			}));
		}
		e.target.value = "";
	};

	const handleRemoveImage = (index: number) => {
		setValues(prev => ({
			...prev,
			additionalImages: prev.additionalImages.filter(
				(_, i) => i !== index
			)
		}));
	};

	const activePricingValue =
		values.pricingMode === "margin" ? values.targetMargin : values.markup;

	const hasAnyValue =
		activePricingValue.trim() !== "" ||
		values.lineItemDiscount.trim() !== "" ||
		values.userPreferences.trim() !== "" ||
		values.additionalImages.length > 0;

	const activePricingError =
		values.pricingMode === "margin"
			? validation.targetMargin
			: validation.markup;

	const hasErrors =
		activePricingError !== "" || validation.lineItemDiscount !== "";

	const handleConfirm = () => {
		if (hasErrors) return;
		hasAnyValue ? onConfirm(values) : onSkip();
	};

	return (
		<ZDrawer
			title=""
			visible={visible}
			onClose={onClose}
			className="z-pricing-configuration-drawer"
			bodyStyle={{ padding: 0 }}
			headerStyle={{ display: "none" }}
			destroyOnClose
			maskClosable={false}
			keyboard={false}
		>
			<div className="h-full flex flex-col justify-between">
				{/* Header */}
				<div className="pricing-config-gradient-header">
					<div className="pricing-config-close-button">
						<ZButton
							onClick={onClose}
							className="bg-transparent border-none cursor-pointer p-0 hover:opacity-70"
						>
							<ZIcon name={EIcons.CLOSE} size="md" fill />
						</ZButton>
					</div>

					<ZTypography.ZTitle
						level={3}
						color={EColors.GRAY}
						colorVariant={9}
						className="font-semibold mb-4"
					>
						Pricing Configuration
					</ZTypography.ZTitle>

					<ZTypography.ZText
						level={1}
						color={EColors.GRAY}
						colorVariant={9}
					>
						Refine how imported items are priced before adding to
						quote.
					</ZTypography.ZText>
				</div>

				{/* Body */}
				<div className="flex-1 overflow-y-auto px-8">
					{error && (
						<div className="bg-red-1 border border-red-4 rounded p-3 mb-4 flex items-center gap-2">
							<ZIcon
								name={EIcons.EXCLAMATIONCIRCLE}
								size="sm"
								color={EColors.RED}
								colorVariant={6}
							/>
							<ZTypography.ZText
								color={EColors.RED}
								colorVariant={7}
							>
								{error}
							</ZTypography.ZText>
						</div>
					)}

					{/* Pricing Fields */}
					<div className="w-175 rounded border border-solid border-gray-4 p-6">
						<div className="grid grid-cols-2 gap-x-4">
							<PricingField
								label="Target Margin (%)"
								value={values.targetMargin}
								error={
									values.pricingMode === "margin"
										? validation.targetMargin
										: undefined
								}
								onChange={handleMarginChange}
								placeholder="%"
								showPercentAddon
								readOnly={values.pricingMode !== "margin"}
								onFocus={handleSwitchToMargin}
								tooltip="Sets the display selling price to achieve this margin before line item discount is applied."
							/>

							<PricingField
								label="Line Item Discount (%)"
								value={values.lineItemDiscount}
								error={validation.lineItemDiscount}
								onChange={handleLineItemDiscountChange}
								placeholder="%"
								showPercentAddon
								tooltip="Applied to each imported line item to calculate final selling price from display selling price."
							/>

							<PricingField
								label="Target Markup (%)"
								value={values.markup}
								error={
									values.pricingMode === "markup"
										? validation.markup
										: undefined
								}
								onChange={handleMarkupChange}
								placeholder="%"
								showPercentAddon
								readOnly={values.pricingMode !== "markup"}
								onFocus={handleSwitchToMarkup}
								tooltip="Sets the display selling price to achieve this markup before line item discount is applied."
							/>
						</div>
					</div>

					{/* Additional Context */}
					<div className="w-175 mt-4">
						<ZTypography.ZText
							level={1}
							color={EColors.GRAY}
							colorVariant={9}
							className="font-medium mb-2 block"
						>
							Additional Context / Rules / Instructions
						</ZTypography.ZText>

						<Input.TextArea
							value={values.userPreferences}
							onChange={handleUserPreferencesChange}
							onKeyDown={handleUndoRedo}
							placeholder="Enter Custom Instructions here"
							autoSize={{ minRows: 7, maxRows: 12 }}
							className="w-full rounded"
						/>

						{/* Suggested instruction pills */}
						<ZTypography.ZText
							level={1}
							color={EColors.GRAY}
							colorVariant={7}
							className="mt-3 mb-2 block"
						>
							Suggested Instructions
						</ZTypography.ZText>
						<div className="flex flex-wrap gap-2">
							{PROMPT_TAGS.map(tag => {
								const isActive = activeTags.has(tag.id);
								const isModified = modifiedTags.has(tag.id);
								return (
									<button
										key={tag.id}
										type="button"
										onClick={() => handleTagToggle(tag)}
										className={`px-3 py-1 rounded-full border border-solid text-xs cursor-pointer transition-colors ${
											isActive
												? "bg-green-1 border-green-5 text-green-6"
												: isModified
												? "bg-orange-1 border-orange-4 text-orange-6"
												: "bg-gray-1 border-gray-4 text-gray-7 hover:border-gray-5"
										}`}
									>
										{!isActive && !isModified && "+"}{" "}
										{tag.label}
									</button>
								);
							})}
						</div>
					</div>

					{/* Additional Images */}
					<div className="w-175 mt-4">
						<ZTypography.ZText
							level={1}
							color={EColors.GRAY}
							colorVariant={9}
							className="font-medium mb-2 block"
						>
							Product Images
						</ZTypography.ZText>

						<div className="border-gray-4 border border-solid bg-gray-2">
							{values.additionalImages.length === 0 ? (
								<label className="flex flex-col items-center justify-center border border-gray-4 border-solid gap-3 cursor-pointer h-20">
									<input
										type="file"
										className="hidden"
										accept={ACCEPTED_IMAGE_TYPES}
										multiple
										onChange={handleImageUpload}
									/>
									<div className="flex items-center gap-1">
										<ZIcon
											name={EIcons.UPLOAD}
											size="sm"
											color={EColors.BLUE}
											colorVariant={6}
										/>
										<ZTypography.ZText
											colorVariant={6}
											color={EColors.BLUE}
										>
											Upload
										</ZTypography.ZText>
									</div>
									<ZTypography.ZText
										level={0.5}
										colorVariant={8}
									>
										Supports: JPG, PNG
									</ZTypography.ZText>
								</label>
							) : (
								<div className="flex flex-col items-center justify-center gap-2 py-3">
									<div className="flex items-center gap-2">
										<ZIcon
											name={EIcons.CHECKCIRCLESOLID}
											size="sm"
											color={EColors.BLUE}
											colorVariant={6}
										/>
										<ZTypography.ZText
											color={EColors.BLUE}
											colorVariant={6}
											weight={EFontWeights.SEMIBOLD}
										>
											Upload completed
										</ZTypography.ZText>
									</div>

									<div className="flex flex-wrap items-center justify-center gap-2">
										{values.additionalImages.map(
											(file, idx) => (
												<div
													key={`${file.name}-${idx}`}
													className="flex items-center gap-2 p-1 bg-blue-1"
												>
													<ZIcon
														name={
															EIcons.FILEATTACHMENTUPLOADED
														}
														size="sm"
														colorVariant={7}
													/>
													<ZTypography.ZText
														colorVariant={6}
														color={EColors.BLUE}
													>
														{file.name}
													</ZTypography.ZText>
													<ZButton
														className="bg-transparent border-none p-0 cursor-pointer hover:opacity-70 flex items-center"
														onClick={() =>
															handleRemoveImage(
																idx
															)
														}
														aria-label={`Remove ${file.name}`}
													>
														<ZIcon
															name={EIcons.CLOSE}
															colorVariant={7}
															fill
														/>
													</ZButton>
												</div>
											)
										)}
									</div>

									<label className="flex items-center gap-1 cursor-pointer mt-1">
										<input
											type="file"
											className="hidden"
											accept={ACCEPTED_IMAGE_TYPES}
											multiple
											onChange={handleImageUpload}
										/>
										<ZIcon
											name={EIcons.UPLOAD}
											size="sm"
											color={EColors.BLUE}
											colorVariant={6}
										/>
										<ZTypography.ZText
											colorVariant={6}
											color={EColors.BLUE}
										>
											Upload more
										</ZTypography.ZText>
									</label>
								</div>
							)}
						</div>
					</div>

					{/* Helper Text */}
					<div className="flex justify-center mt-4">
						<ZTypography.ZText
							className={`text-gray-7 font-normal text-0 leading-[18px] tracking-[0px] w-175 ${
								!hasAnyValue ? "text-left" : "text-center"
							}`}
						>
							{!hasAnyValue
								? "You can skip this step if your spreadsheet or PDF already contains these values and you'd like to keep them unchanged."
								: "You can skip this step if your spreadsheet/pdf includes these values."}
						</ZTypography.ZText>
					</div>
				</div>

				{/* Footer */}
				<div className="py-4 px-6 border-t border-gray-3 flex-shrink-0 flex flex-col items-center gap-3">
					<div className="flex justify-center gap-3">
						<ZButton
							onClick={onClose}
							className="pricing-config-back-button"
						>
							Back
						</ZButton>

						<ZButton
							onClick={handleConfirm}
							disabled={hasErrors}
							className="pricing-config-import-button w-60"
						>
							<ZIcon
								name={EIcons.STARICON}
								size="sm"
								className="-mr-2 mt-1"
							/>

							{hasAnyValue
								? "Apply settings and Import"
								: "Skip this step and import"}
						</ZButton>
					</div>

					<ZTypography.ZText className="text-gray-7 font-normal text-[10px] leading-[18px] tracking-[0px] text-center">
						AI can make mistakes. Please double check the response.
					</ZTypography.ZText>
				</div>
			</div>
		</ZDrawer>
	);
};

export default ZPricingConfigurationDrawer;
